import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const insights = [
  {
    id: 1,
    type: 'bullish',
    title: 'Bitcoin Breaking Resistance',
    description: 'BTC has broken through the $67,000 resistance level with strong volume. Historical patterns suggest potential 15% upside.',
    confidence: 87,
    timestamp: '2 hours ago',
    asset: 'BTC',
  },
  {
    id: 2,
    type: 'bearish',
    title: 'ETH Overbought Signal',
    description: 'Ethereum RSI indicates overbought conditions. Consider taking partial profits or setting stop-losses.',
    confidence: 72,
    timestamp: '4 hours ago',
    asset: 'ETH',
  },
  {
    id: 3,
    type: 'neutral',
    title: 'Market Consolidation',
    description: 'Overall crypto market is consolidating. Low volatility period expected for the next 24-48 hours.',
    confidence: 65,
    timestamp: '6 hours ago',
    asset: 'Market',
  },
];

const InsightCard = ({ insight }) => {
  const typeConfig = {
    bullish: {
      icon: 'fa-solid fa-arrow-trend-up',
      color: 'text-success',
      bgColor: 'bg-success/10',
      badgeVariant: 'gain',
    },
    bearish: {
      icon: 'fa-solid fa-arrow-trend-down',
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      badgeVariant: 'loss',
    },
    neutral: {
      icon: 'fa-solid fa-minus',
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      badgeVariant: 'secondary',
    },
  };

  const config = typeConfig[insight.type];

  return (
    <div className="p-4 rounded-lg border border-border bg-card/50 hover:bg-card transition-colors">
      <div className="flex items-start gap-3">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", config.bgColor)}>
          <i className={cn(config.icon, config.color, "text-sm")}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm truncate">{insight.title}</h4>
            <Badge variant="secondary" className="text-xs shrink-0">{insight.asset}</Badge>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">{insight.description}</p>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <i className="fa-solid fa-brain text-primary text-xs"></i>
              <span className="text-xs text-muted-foreground">{insight.confidence}% confidence</span>
            </div>
            <span className="text-xs text-muted-foreground">{insight.timestamp}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AIInsights = ({ className }) => {
  return (
    <Card className={cn("h-full", className)} data-testid="ai-insights-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <i className="fa-solid fa-robot text-primary text-sm"></i>
            </div>
            <CardTitle className="text-base">AI Insights</CardTitle>
          </div>
          <Badge variant="secondary" className="text-xs">
            <i className="fa-solid fa-sparkles mr-1"></i>
            Live
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight) => (
          <InsightCard key={insight.id} insight={insight} />
        ))}
        <Button variant="outline" className="w-full mt-2" size="sm">
          <i className="fa-solid fa-lightbulb mr-2"></i>
          View All Insights
        </Button>
      </CardContent>
    </Card>
  );
};

export default AIInsights;
